<script setup lang="ts">
import { ref, onMounted, defineProps, computed, onBeforeUnmount } from "vue";
import { displayService } from '@/_services';

const props = defineProps<{
  config: any;
  backgroundColor: string;
  valuesColor: string;
  textColor: string;
  flagTxtColor: string;
  separator: string;
  separatorColor: string;
  separatorPadding: number;
  tickerSpeed: any;
}>();

const ticker = ref<any[]>([]);
const isOnline = ref(navigator.onLine);

const codeToFlag = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  JPY: 'jpy',
  CAD: 'ca',
  CHF:'ch',
  AED:'aed',
  SAR:'sa',
  QAR:'qa',
  KWD:'kw',
  BHD:'bh',
  LYD:'lyd',
  CNY:'cny',
};

const saveToLocalStorage = (data: any[]) => {
  try {
    localStorage.setItem('exchangeData', JSON.stringify(data));
    localStorage.setItem('exchangeDataTimestamp', Date.now().toString());
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem('exchangeData');
    const timestamp = localStorage.getItem('exchangeDataTimestamp');
    
    if (data && timestamp) {
      const parsedData = JSON.parse(data);
      const dataAge = Date.now() - parseInt(timestamp);
      
      // Only use cached data if it's less than 24 hours old
      if (dataAge < 24 * 60 * 60 * 1000) {
        return parsedData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

const getExchangeData = async () => {
  try {
    if (isOnline.value) {
      const response = await displayService.getExchangeData();
      const changes = response.changes || [];
      const updatedTicker = await Promise.all(
        changes.map(async (item: any) => {
          let flagSrc = '';
          try {
            const code = String(item.code);
            const flagFile = (codeToFlag as Record<string, string>)[code] || code.toLowerCase();
            const flagModule = await import(`@/assets/images/flags/${flagFile}.svg`);
            flagSrc = flagModule.default;
          } catch (error) {
            flagSrc = '';
          }
          return {
            ...item,
            flagSrc,
          };
        })
      );
      ticker.value = updatedTicker;
      saveToLocalStorage(updatedTicker);
    } else {
      const cachedData = loadFromLocalStorage();
      if (cachedData) {
        ticker.value = cachedData;
      } else {
        ticker.value = [{
          code: 'SERVICE',
          sell: 'MOMENTANEMENT',
          buy: 'INDISPONIBLE',
          flagSrc: ''
        }];
      }
    }
  } catch (err) {
    console.error("Error loading exchange data:", err);
    const cachedData = loadFromLocalStorage();
    if (cachedData) {
      ticker.value = cachedData;
    } else {
      ticker.value = [{
        code: 'SERVICE',
        sell: 'MOMENTANEMENT',
        buy: 'INDISPONIBLE',
        flagSrc: ''
      }];
    }
  }
};

const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine;
  console.log(`Connection status changed: ${isOnline.value ? "Online" : "Offline"}`);
  getExchangeData();
};

const formatNumber = (num: number) => {
  return Math.trunc(num * 1000) / 1000;
}

// responsive sizing
const flagHeight = computed(() => {
  const height = props.config?.height || 40;
  return Math.floor(height * 0.625); // 62.5% of the ticker height
});

const flagWidth = computed(() => {
  const height = props.config?.height || 40;
  return Math.floor(height); // 100% of the ticker height
});

const codeFontSize = computed(() => {
  const height = props.config?.height || 40;
  return Math.floor(height * 0.35) + 'px'; // 35% of the ticker height
});

const valueFontSize = computed(() => {
  const height = props.config?.height || 40;
  return Math.floor(height * 0.45) + 'px'; // 45% of the ticker height
});

const labelFontSize = computed(() => {
  const height = props.config?.height || 40;
  return Math.floor(height * 0.3) + 'px'; //30% of the ticker height
});

onMounted(() => {
  updateOnlineStatus();
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
});

onBeforeUnmount(() => {
  window.removeEventListener('online', updateOnlineStatus);
  window.removeEventListener('offline', updateOnlineStatus);
});
</script>

<script lang="ts">
export default {
  name: "ExchangeTicker",
};
</script>

<template>

  <div
    class="exchange-ticker-widget"
    :style="{
      backgroundColor: backgroundColor,
      fontSize: valueFontSize,
      overflow: 'hidden',
      alignItems: 'center',
      display: 'flex',
    }"
  >
    <div class="ticker-track-horizontal" :style="{ animationDuration: tickerSpeed + 's', height: '100%' }">
      <div class="ticker-content" style="height: 100%">
        <template v-for="(item, index) in ticker" :key="index">
          <span v-if="index > 0" class="ticker-separator-container">
            <span
              class="ticker-separator"
              :style="{
                color: separatorColor,
                padding: `0 ${separatorPadding}px`,
              }"
            >{{ separator }}</span>
          </span>
          <span class="ticker-item" :style="{ gap: '16px', minHeight: '40px' }">
            <div class="currency-container">
              <img
                v-if="item.flagSrc"
                :src="item.flagSrc"
                class="ticker-flag-img"
                alt="flag"
                :style="{ height: flagHeight + 'px', width: flagWidth + 'px' }"
              />
              <span class="ticker-code" :style="{ color: flagTxtColor, fontSize: codeFontSize, fontWeight: 'bold' }">{{
                item.code
              }}</span>
            </div>
            <span class="ticker-values" :style="{ gap: '15px' }">
              <span class="value-container">
                <span class="ticker-value" :style="{ color: valuesColor, fontSize: valueFontSize, fontWeight: 'bold' }">{{
                  item.buy
                }}</span>
                <span class="value-label" :style="{ color: textColor, fontSize: labelFontSize, textTransform: 'uppercase' }"
                  >ACHAT</span
                >
              </span>
              <span class="value-container">
                <span class="ticker-value" :style="{ color: valuesColor, fontSize: valueFontSize, fontWeight: 'bold' }">{{
                  item.sell
                }}</span>
                <span class="value-label" :style="{ color: textColor, fontSize: labelFontSize, textTransform: 'uppercase' }"
                  >VENTE</span
                >
              </span>
            </span>
          </span>
        </template>
      </div>
      <div class="ticker-content" aria-hidden="true" style="height: 100%">
        <template v-for="(item, index) in ticker" :key="index">
          <span class="ticker-separator-container">
            <span
              class="ticker-separator"
              :style="{
                color: separatorColor,
                padding: `0 ${separatorPadding}px`,
              }"
            >{{ separator }}</span>
          </span>
          <span class="ticker-item" :style="{ gap: '16px', minHeight: '40px' }">
            <div class="currency-container">
              <img
                v-if="item.flagSrc"
                :src="item.flagSrc"
                class="ticker-flag-img"
                alt="flag"
                :style="{ height: flagHeight + 'px', width: flagWidth + 'px' }"
              />
              <span class="ticker-code" :style="{ color: flagTxtColor, fontSize: codeFontSize, fontWeight: 'bold' }">{{
                item.code
              }}</span>
            </div>
            <span class="ticker-values" :style="{ gap: '15px' }">
              <span class="value-container">
                <span class="ticker-value" :style="{ color: valuesColor, fontSize: valueFontSize, fontWeight: 'bold' }">{{
                  item.buy
                }}</span>
                <span class="value-label" :style="{ color: textColor, fontSize: labelFontSize, textTransform: 'uppercase' }"
                  >Achat</span
                >
              </span>
              <span class="value-container">
                <span class="ticker-value" :style="{ color: valuesColor, fontSize: valueFontSize, fontWeight: 'bold' }">{{
                  item.sell
                }}</span>
                <span class="value-label" :style="{ color: textColor, fontSize: labelFontSize, textTransform: 'uppercase' }"
                  >Vente</span
                >
              </span>
            </span>
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.exchange-ticker-widget {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex !important;
  align-items: center;
  background: transparent;
}

.ticker-track-horizontal {
  display: flex !important;
  white-space: nowrap;
  width: max-content;
  align-items: center;
  animation: ticker-horizontal 12s linear infinite;
  height: 100%;
  line-height: normal;
  will-change: transform;
}

.ticker-content {
  display: inline-flex !important;
  flex-wrap: nowrap;
  align-items: center;
  height: 100%;
  white-space: nowrap;
}

.ticker-separator-container {
  display: flex !important;
  align-items: center;
  height: 100%;
}

.ticker-separator {
  display: flex !important;
  align-items: center;
  height: 100% !important; 
}

.ticker-item {
  display: flex !important;
  align-items: center;
  /* margin: 0 5px; */
}

.currency-container {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: center;
}

.ticker-flag-img {
  object-fit: contain !important;
  display: block !important;
}

.ticker-code {
  font-weight: bold;
  display: block !important;
}

.ticker-values {
  display: flex !important;
  align-items: center;
}

.value-container {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.ticker-value {
  font-weight: bold;
  display: block !important;
}

.value-label {
  text-transform: uppercase;
  margin-top: 2px;
  display: block !important;
}

@keyframes ticker-horizontal {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}

:fullscreen .ticker-track-horizontal {
  animation: ticker-horizontal 12s linear infinite !important;
  width: max-content !important;
}

</style>
